import { useEffect, useRef, useState } from "react";
import {
  Box,
  Input,
  CloseButton,
  List,
  ListItem,
  HStack,
  Spinner,
} from "@chakra-ui/react";
import { AiOutlineSearch } from "react-icons/ai";
import { AxiosInstance } from "axios";
import { isNumeric, toIndiaMobileWithCountryCode } from "../utils/util";

export type Result = {
  id: string;
  data: any;
  value: string;
};

interface Props {
  placeholder: string;
  onSelect: (value: any) => void;
  onClear?: () => void;
  onChange: (value: string) => void;
  queryUrl: string;
  axios: AxiosInstance;
  hideSearchIcon?: boolean;
  idField: string;
  valueField: string;
  acceptMobileNumber?: boolean;
  value: string;
  isDisabled?: boolean;
  searchListWidth?: string;
  /** Rich renderer for a dropdown row; falls back to the plain value text */
  renderItem?: (data: any) => React.ReactNode;
}

const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_RESULTS_MAX_HEIGHT = "320px";

const GaraazSearch = (props: Props) => {
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setResults([]);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (props.acceptMobileNumber) {
      if (isNumeric(value) && value.length > 10) {
        return;
      }
    }
    props.onChange(value);
    if (value.trim() === "") {
      setResults([]);
      return;
    }
    typingTimeoutRef.current = setTimeout(() => {
      if (value.trim().length > 0) {
        fetchData(value);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const fetchData = async (text: string) => {
    setLoading(true);
    let results = new Array<Result>();
    let url = `${props.queryUrl}${text}`;
    if (props.acceptMobileNumber && isNumeric(text)) {
      // Partial typing still prefixes 91; full 10-digit uses the shared helper
      url = `${props.queryUrl}${toIndiaMobileWithCountryCode(text) ?? `91${text}`}`;
    }
    const { data } = await props.axios.get(url);
    for (let i = 0; i < data.length; i++) {
      results.push({
        id: data[i][props.idField],
        data: data[i],
        value: data[i][props.valueField],
      });
    }
    setResults(results);
    setLoading(false);
  };

  const handleClear = () => {
    setResults([]);
    if (props.onClear) {
      props.onClear();
    }
  };

  return (
    <Box
      ref={containerRef}
      border="1px"
      w="100%"
      borderColor="gray.300"
      borderRadius="7px"
      position={"relative"}
    >
      <HStack alignItems="center" pr={2}>
        {!props.hideSearchIcon && <AiOutlineSearch />}
        <Input
          placeholder={props.placeholder}
          value={props.value}
          onChange={handleInputChange}
          size="md"
          autoFocus
          border="none"
          _placeholder={{ fontSize: "sm" }}
          isDisabled={props.isDisabled}
        />
        {(loading && <Spinner size="sm" />) ||
          (!!props?.value?.length && (
            <CloseButton onClick={handleClear} size="md" />
          ))}
      </HStack>
      {results.length > 0 && (
        <List
          bg="white"
          shadow="lg"
          mt="1"
          py="1"
          px="0"
          rounded="lg"
          border="1px solid"
          borderColor="gray.100"
          style={{ position: "absolute" }}
          top={10}
          left={0}
          zIndex="dropdown"
          width={props?.searchListWidth || "100%"}
          maxH={SEARCH_RESULTS_MAX_HEIGHT}
          overflowY="auto"
        >
          {results.map((result, index) => (
            <ListItem
              key={result.id}
              px="3"
              py={props.renderItem ? "2" : "1"}
              cursor="pointer"
              borderTop={index > 0 ? "1px solid" : undefined}
              borderColor="gray.100"
              _hover={{ bg: "gray.50" }}
              onClick={() => {
                props.onSelect(result.data);
                props.onChange(result.value);
                setResults([]);
              }}
            >
              {props.renderItem ? props.renderItem(result.data) : result.value}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default GaraazSearch;
